/**
 * On-demand loader for the Razorpay checkout script. checkout.js must come
 * from Razorpay's CDN (it cannot be bundled), so we inject it the first time
 * a checkout is initiated and reuse it afterwards.
 */
const RAZORPAY_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

/**
 * The accent Razorpay Checkout paints its window in: the brand aubergine
 * (`--color-accent`). A hex, not a CSS variable — the window is Razorpay's
 * own page and cannot read our tokens. Every checkout uses this one value.
 */
export const RAZORPAY_THEME_COLOR = '#4B2C6B';

let loader: Promise<void> | null = null;

export function loadRazorpayScript(): Promise<void> {
  // Already available (previous load or a static tag).
  if (typeof window !== 'undefined' && (window as { Razorpay?: unknown }).Razorpay) {
    return Promise.resolve();
  }
  if (loader) return loader;

  loader = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = RAZORPAY_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loader = null; // allow a retry on the next attempt
      // The code, not this English text, is what the screen shows (translated).
      reject(Object.assign(new Error('Payments are unavailable right now. Check your connection and try again.'), { code: 'PAYMENTS_UNAVAILABLE' }));
    };
    document.head.appendChild(script);
  });
  return loader;
}

/** What Razorpay hands back when a subscription is authorised. */
export interface RazorpaySubscriptionResponse {
  razorpay_payment_id: string;
  razorpay_signature: string;
  razorpay_subscription_id: string;
}

export interface RazorpaySubscriptionCheckout {
  razorpayKeyId: string;
  subscriptionId: string;
  /** The line shown in the Razorpay window. */
  description: string;
}

/**
 * Opens Razorpay Checkout for a recurring subscription (opened by
 * `subscription_id`, not an order) and resolves with the signed response, or
 * null when the customer closes the window. Loads checkout.js on demand.
 *
 * No `amount` or `currency` is passed: Checkout's `amount` is in paise, and
 * for a subscription it shows the plan's own amount, the one actually
 * charged. Passing rupees showed a price a hundred times too small.
 */
export async function openRazorpaySubscription(checkout: RazorpaySubscriptionCheckout): Promise<RazorpaySubscriptionResponse | null> {
  await loadRazorpayScript();
  const Razorpay = (window as Window & { Razorpay?: new (o: Record<string, unknown>) => { open: () => void } }).Razorpay;
  if (!Razorpay) throw Object.assign(new Error('Payments are unavailable right now. Check your connection and try again.'), { code: 'PAYMENTS_UNAVAILABLE' });
  return new Promise<RazorpaySubscriptionResponse | null>((resolve) => {
    new Razorpay({
      key: checkout.razorpayKeyId,
      subscription_id: checkout.subscriptionId,
      name: 'UniPostudio',
      description: checkout.description,
      theme: { color: RAZORPAY_THEME_COLOR },
      handler: (response: RazorpaySubscriptionResponse): void => resolve(response),
      modal: { ondismiss: (): void => resolve(null) },
    }).open();
  });
}
